import React, { useEffect, useMemo, useRef, useState } from "react";
import { Building2, Check, ChevronDown, Search } from "lucide-react";

/**
 * Collapsed, searchable apartment picker.
 *
 * Renders as a single field showing the chosen apartment (or a placeholder)
 * and only expands the list on tap. An always-visible list is fine for three
 * apartments and unusable at thirty — it pushed the flat-number and
 * instructions fields off-screen, so the customer had to scroll past every
 * apartment to reach the rest of the form.
 *
 * Filtering is client-side over the already-loaded list. The customer-facing
 * endpoint returns active apartments for one tenant capped at 500, so this
 * stays instant with no per-keystroke round-trip. If a tenant ever exceeds
 * that cap, switch to the endpoint's `q` parameter — it already supports
 * server-side search over the same fields.
 *
 * Presentational and self-contained: it owns only its open/query state, and
 * reports the selection upward. Nothing here is tenant-aware — the caller
 * supplies whichever list it fetched.
 *
 * Props:
 *   apartments  — [{id, name, address, city, pincode}]
 *   value       — the selected apartment object, or null
 *   onChange    — (apartment) => void
 *   placeholder — collapsed-field text when nothing is selected
 *   testIdPrefix
 */
export default function ApartmentSelect({
  apartments = [],
  value = null,
  onChange,
  placeholder = "Select your apartment",
  testIdPrefix = "apartment-select",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  // Close on outside click (same pattern as the other dropdowns in the app).
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Focus the search box on open so a customer who knows their block can just
  // start typing. Deferred a frame — the input doesn't exist until this
  // render commits.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return apartments;
    return apartments.filter((a) =>
      [a.name, a.address, a.city, a.pincode]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [apartments, query]);

  const subtitle = (a) => [a.address, a.city, a.pincode].filter(Boolean).join(", ");

  const choose = (a) => {
    onChange?.(a);
    setOpen(false);
    // Reset so reopening starts from the full list rather than the last
    // search — otherwise the previously chosen block can look like the only
    // one available.
    setQuery("");
  };

  return (
    <div className="relative" ref={wrapRef} data-testid={testIdPrefix}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full h-12 px-3 rounded-2xl border border-neutral-200 bg-white flex items-center gap-2 text-left"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={`${testIdPrefix}-trigger`}>
        <Building2 className={`w-4 h-4 shrink-0 ${value ? "text-brand-sky" : "text-neutral-400"}`}/>
        <span className="min-w-0 flex-1">
          {value ? (
            <>
              <span className="block text-sm font-bold truncate" data-testid={`${testIdPrefix}-selected`}>
                {value.name}
              </span>
              {subtitle(value) && (
                <span className="block text-[11px] text-neutral-500 truncate">{subtitle(value)}</span>
              )}
            </>
          ) : (
            <span className="text-sm text-neutral-500">{placeholder}</span>
          )}
        </span>
        <ChevronDown className={`w-4 h-4 text-neutral-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>

      {open && (
        <div className="absolute z-40 mt-1.5 w-full bg-white border border-neutral-200 rounded-2xl shadow-xl overflow-hidden"
             role="listbox" data-testid={`${testIdPrefix}-panel`}>
          <div className="p-2 border-b border-neutral-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400"/>
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, area or pincode…"
                className="w-full h-10 pl-9 pr-3 rounded-xl border border-neutral-200 text-sm"
                data-testid={`${testIdPrefix}-search`}
              />
            </div>
          </div>

          {/* Capped height: the whole point is that the form below stays
              reachable no matter how many apartments a tenant has. */}
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-4 text-sm text-neutral-500 text-center" data-testid={`${testIdPrefix}-no-results`}>
                No apartments match that search.
              </div>
            ) : filtered.map((a) => {
              const active = value?.id === a.id;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => choose(a)}
                  className={`w-full text-left p-3 flex items-start gap-3 border-b border-neutral-100 last:border-b-0 ${active ? "bg-brand-sky/10" : "hover:bg-neutral-50"}`}
                  role="option"
                  aria-selected={active}
                  data-testid={`${testIdPrefix}-option-${a.id}`}>
                  <Building2 className={`w-4 h-4 mt-0.5 shrink-0 ${active ? "text-brand-sky" : "text-neutral-400"}`}/>
                  <span className="min-w-0 flex-1">
                    <span className={`block text-sm font-bold truncate ${active ? "text-brand-sky" : ""}`}>{a.name}</span>
                    {subtitle(a) && (
                      <span className="block text-xs text-neutral-500 break-words">{subtitle(a)}</span>
                    )}
                  </span>
                  {active && <Check className="w-4 h-4 text-brand-sky shrink-0 mt-0.5"/>}
                </button>
              );
            })}
          </div>

          {/* Only meaningful once the list is long enough to scroll past. */}
          {apartments.length > 8 && (
            <div className="px-3 py-2 border-t border-neutral-100 text-[10px] text-neutral-400"
                 data-testid={`${testIdPrefix}-count`}>
              {filtered.length} of {apartments.length} apartments
            </div>
          )}
        </div>
      )}
    </div>
  );
}
